import { CheckCircle, Grid, Loader } from 'phosphor-icons';
import { interpolateStringWithData } from 'utils';
import { c } from '../../../utils/tailwind-merge';

import { BadgePropsData } from '../../atoms/Badge/Badge.types';
import { ButtonV2 } from '../../atoms/Button/Button.v2';
import { LinkV2 } from '../../atoms/Link/Link.v2';
import { Price } from '../../atoms/Price/Price';
import { BadgeList } from '../../molecules/BadgeList/BadgeList';
import { PublisherInfo } from '../../molecules/PublisherInfo/PublisherInfo';
import { ReviewStars } from '../../molecules/ReviewStars/ReviewStars';
import { RichText } from '../../molecules/RichText/RichText';
import {
  ProductInfoProps,
  ProductInfoPropsActions,
  ProductInfoPropsMicrocopy,
} from './ProductInfo.types';

const flexItemBadge = 'flex items-center';

const VerifiedSolutionsTooltipContent = ({
  title,
  benefit1,
  benefit2,
  benefit3,
  cta,
}: ProductInfoPropsMicrocopy['verifiedSolutionsTooltip']) => (
  <>
    {title && (
      <RichText
        html={title}
        className="text-sm [&_a]:text-sm [&_a]:no-underline [&_span]:mb-0 [&_span]:text-grey-700"
      />
    )}
    {benefit1 && (
      <div className="px-3 py-2 pb-0">
        <div className={flexItemBadge}>
          <CheckCircle
            className="mr-3 mt-1 inline-block text-blue"
            size={25}
            aria-hidden="true"
          />
          <RichText
            html={benefit1}
            className="py-1.5 text-sm [&_a]:no-underline"
            type="p"
          />
        </div>
        {benefit2 && (
          <div className={flexItemBadge}>
            <div className="mr-1 pt-1">
              <Loader />
            </div>
            <RichText html={benefit2} className="py-1.5 text-sm" />
          </div>
        )}
        {benefit3 && (
          <div className={flexItemBadge}>
            <div className="mr-1 pt-1">
              <Grid />
            </div>
            <RichText html={benefit3} className="py-1.5 text-sm" />
          </div>
        )}
      </div>
    )}
    {cta && (
      <div className="mt-1 py-1">
        <RichText
          html={cta}
          className="py-1.5 text-sm [&_a]:no-underline [&_p]:mb-0"
          type="p"
        />
      </div>
    )}
  </>
);

export const renderBadges = ({ isUpm, isVerifiedSolutions, microcopy }) => {
  const badges: BadgePropsData[] = [];

  if (isUpm) {
    badges.push({
      text: microcopy.upm,
      popover: (
        <RichText
          html={microcopy.upmTooltip}
          className="text-sm [&_a]:text-sm [&_a]:no-underline [&_p]:mb-0 [&_span]:mb-0 [&_span]:text-grey-700"
        />
      ),
      interactive: true,
    });
  }

  if (isVerifiedSolutions) {
    badges.push({
      text: microcopy.verifiedSolutions,
      popover: VerifiedSolutionsTooltipContent(
        microcopy.verifiedSolutionsTooltip
      ),
      interactive: true,
    });
  }

  return <BadgeList badges={badges} />;
};

const renderCtaButton = ({
  type,
  nbOfPackages,
  isEntitled,
  microcopy,
  onClickProductInfo,
}: Pick<
  ProductInfoProps,
  'type' | 'nbOfPackages' | 'isEntitled' | 'microcopy'
> &
  ProductInfoPropsActions & { className?: string }) => {
  const label =
    type === 'single-package'
      ? isEntitled
        ? microcopy.openInUnity
        : microcopy.addToMyAssetSinglePackage
      : isEntitled
        ? microcopy.seeOwnedPackages
        : interpolateStringWithData(microcopy.addToMyAssetMultiPackages, {
            number: nbOfPackages,
          });

  return (
    <ButtonV2
      label={label}
      variant={!isEntitled ? 'primary' : 'secondary-blue-monochrome'}
      size="large"
      shape="square"
      onClick={onClickProductInfo}
      className="w-full"
    />
  );
};

const renderFavoriteButton = ({
  isFavorite,
  microcopy,
  onFavorite,
}: Pick<ProductInfoProps, 'isFavorite' | 'microcopy' | 'onFavorite'>) => {
  if (isFavorite == null || !onFavorite) {
    return null;
  }

  return (
    <ButtonV2
      key={isFavorite ? 'favorite' : 'not-favorite'}
      variant={isFavorite ? 'primary' : 'secondary-blue-monochrome'}
      size="large"
      shape="square"
      iconLeft="BookmarkSimple"
      iconWeight={isFavorite ? 'fill' : 'bold'}
      iconSize={20}
      aria-label={
        isFavorite ? microcopy.removeFromFavorites : microcopy.addToFavorites
      }
      aria-pressed={isFavorite}
      onClick={() => onFavorite()}
    />
  );
};

const renderEulaMessage = ({
  customEula,
  microcopy,
}: Pick<ProductInfoProps, 'customEula' | 'microcopy'>) => {
  const isStandardEula = !customEula;
  const sharedRichTextProps = {
    className: 'text-xs text-grey-700 [&_p]:mb-0',
    linkComponent: ({ href, children, target, rel }) => (
      <LinkV2
        href={href}
        openInANewTab={target === '_blank'}
        variant="link-in-text-primary"
        rel={rel}
      >
        {children}
      </LinkV2>
    ),
  };
  return isStandardEula ? (
    <RichText html={microcopy.standardEulaMessage} {...sharedRichTextProps} />
  ) : (
    <RichText
      html={interpolateStringWithData(microcopy.customEulaMessage, {
        custom: customEula ?? '',
      })}
      {...sharedRichTextProps}
    />
  );
};

export const ProductInfo = ({
  variant = 'default',
  isUpm,
  isVerifiedSolutions,
  productTitle,
  publisherName,
  publisherLink,
  isCertifiedPublisher,
  publisherAvatarUrl,
  price,
  type,
  isEntitled,
  nbOfPackages,
  microcopy,
  className = '',
  onClickProductInfo,
  onCallToActionMount,
  customEula,
  reviews,
  isFavorite,
  onFavorite,
  onClickReviewsLink,
  isReviewsEnabled = false,
}: ProductInfoProps) => {
  const isPreviewMode = !onClickProductInfo;
  return variant === 'default' ? (
    <div
      data-test={'product-info-section'}
      className={c('flex flex-col gap-y-4', className)}
    >
      {renderBadges({ isUpm, isVerifiedSolutions, microcopy })}

      <h3
        data-test="product-title"
        className="text-gray-900 text-xl font-semibold leading-8"
      >
        {productTitle}
      </h3>

      {isReviewsEnabled && reviews && microcopy.reviewStars && (
        <ReviewStars
          value={reviews.value}
          max={reviews.max}
          reviews={reviews.reviews}
          microcopy={microcopy.reviewStars}
          variant="small"
          showReviewsLink={true}
          onClickReviewsLink={onClickReviewsLink}
        />
      )}

      <PublisherInfo
        showAvatar={true}
        isCertified={isCertifiedPublisher}
        publisherName={publisherName}
        publisherLink={publisherLink}
        publisherAvatarUrl={publisherAvatarUrl}
        microcopy={{ certifiedPublisher: microcopy.certifiedPublisher }}
      />

      <Price {...price} microcopy={microcopy.price} />

      {isPreviewMode ? (
        <div ref={onCallToActionMount} />
      ) : (
        <>
          <div ref={onCallToActionMount} className="flex gap-2">
            {renderCtaButton({
              type,
              isEntitled,
              nbOfPackages,
              microcopy,
              onClickProductInfo,
            })}
            {renderFavoriteButton({ isFavorite, microcopy, onFavorite })}
          </div>

          {!isEntitled && renderEulaMessage({ customEula, microcopy })}
        </>
      )}
    </div>
  ) : (
    <div
      className={c(
        'fixed bottom-0 left-0 right-0 z-50 border border-grey-300 bg-grey-100 lg:fixed lg:bottom-auto lg:top-0',
        className
      )}
    >
      <div className="lg-container flex w-full items-start justify-between py-3">
        <div className="hidden flex-col lg:flex">
          <h3 className="text-gray-900 text-md font-medium leading-7">
            {productTitle}
          </h3>
          <div className="flex items-center">
            <PublisherInfo
              size="small"
              showAvatar={false}
              isCertified={isCertifiedPublisher}
              publisherName={publisherName}
              publisherLink={publisherLink}
              publisherAvatarUrl={publisherAvatarUrl}
              microcopy={{ certifiedPublisher: microcopy.certifiedPublisher }}
            />
          </div>
        </div>

        <div className="flex w-full flex-col gap-2 lg:max-w-[496px]">
          <div className="flex w-full items-center justify-between gap-4 lg:gap-8">
            <Price {...price} microcopy={microcopy.price} />

            {isPreviewMode ? (
              <div ref={onCallToActionMount} />
            ) : (
              <div ref={onCallToActionMount} className="flex gap-2 lg:w-full">
                {renderCtaButton({
                  type,
                  isEntitled,
                  nbOfPackages,
                  microcopy,
                  onClickProductInfo,
                })}
                {renderFavoriteButton({
                  isFavorite,
                  microcopy,
                  onFavorite,
                })}
              </div>
            )}
          </div>
          {!isPreviewMode &&
            !isEntitled &&
            renderEulaMessage({ customEula, microcopy })}
        </div>
      </div>
    </div>
  );
};
